import React, { useEffect, useMemo, useState, useRef } from "react"
import {
    Box,
    Button,
    Container,
    Flex,
    Grid,
    Heading,
    HStack,
    Image,
    Text,
    VStack,
    useColorModeValue,
    Badge,
    SimpleGrid,
    Tag,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Spinner,
} from "@chakra-ui/react"
import {
    DocumentTextIcon,
    ChartBarIcon,
    CheckCircleIcon,
    EyeIcon,
    StarIcon,
    ArrowRightIcon,
    ArrowDownTrayIcon,
    WrenchScrewdriverIcon,
    RocketLaunchIcon,
    Squares2X2Icon,
} from "@heroicons/react/24/outline"

import { Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"

// Use Active Resume Builder API to show how many builders are available
import {
    fetchCandidateActiveResumeBuilderAsync,
    selectActiveResumebuilder,
} from "../../candidateDashboard/candidateSlice/CandidateActiveResumeBuilder/CandidateActiveResumeBuilderSlice"

// Reuse the same template components and dummy data used in the dashboard
import ProfessionalTemplate from "./templates/ProfessionalTemplate"
import MinimalTemplate from "./templates/MinimalTemplate"
import ModernTemplate from "./templates/ModernTemplate"
import CreativeTemplate from "./templates/CreativeTemplate"
import SimpleTemplate from "./templates/SimpleTemplate"
import ExecutiveTemplate from "./templates/ExecutiveTemplate"
import AchieverTemplate from "./templates/AchieverTemplate"
import ImpactTemplate from "./templates/ImpactTemplate"
import { dummyData } from "./dummyData"

// Template preview images (same assets as dashboard)
import professional from "./images/professionaltemplate.jpeg"
import creative from "./images/creative.webp"
import minimal from "./images/minimal.jpeg"
import modern from "./images/modern.png"
import simple from "./images/simple.png"
import Executive from "./images/Executive.png"
import Achiever from "./images/Achiever.jpg"
import Impact from "./images/Impact.jpg"

const templateImages = {
    professional,
    creative,
    minimal,
    modern,
    simple,
    Executive,
    Achiever,
    Impact,
}

const ResumeBuilderHome = () => {
    const dispatch = useDispatch()
    const activeResumeBuilderData = useSelector(selectActiveResumebuilder)

    const [selectedTemplateKey, setSelectedTemplateKey] = useState("professional")
    const [isPreviewLoading, setIsPreviewLoading] = useState(false)
    const previewRef = useRef(null)
    const { isOpen, onOpen, onClose } = useDisclosure()

    // Color tokens (hooks at top-level only)
    const bgSoft = useColorModeValue("gray.50", "gray.800")
    const cardBg = useColorModeValue("white", "gray.700")
    const textColor = useColorModeValue("gray.800", "white")
    const muted = useColorModeValue("gray.600", "gray.400")
    const borderSoft = useColorModeValue("gray.200", "whiteAlpha.200")

    useEffect(() => {
        // Public page: if user is authenticated, this will fetch data; otherwise it's a no-op
        dispatch(fetchCandidateActiveResumeBuilderAsync()).catch(() => { })
    }, [dispatch])

    const resumeTemplates = useMemo(
        () => [
            {
                id: 1,
                name: "Professional",
                category: "Corporate",
                popularity: "High",
                color: "blue.500",
                image: templateImages.professional,
                component: ProfessionalTemplate,
                key: "professional",
            },
            {
                id: 2,
                name: "Creative",
                category: "Design",
                popularity: "Medium",
                color: "purple.500",
                image: templateImages.creative,
                component: CreativeTemplate,
                key: "creative",
            },
            {
                id: 3,
                name: "Minimal",
                category: "All-purpose",
                popularity: "High",
                color: "gray.500",
                image: templateImages.minimal,
                component: MinimalTemplate,
                key: "minimal",
            },
            {
                id: 4,
                name: "Modern",
                category: "Tech",
                popularity: "Medium",
                color: "green.500",
                image: templateImages.modern,
                component: ModernTemplate,
                key: "modern",
            },
            {
                id: 5,
                name: "Simple",
                category: "Entry-level",
                popularity: "High",
                color: "red.500",
                image: templateImages.simple,
                component: SimpleTemplate,
                key: "simple",
            },
            {
                id: 6,
                name: "Executive",
                category: "Leadership",
                popularity: "High",
                color: "orange.500",
                image: templateImages.Executive,
                component: ExecutiveTemplate,
                key: "Executive",
            },
            {
                id: 7,
                name: "Achiever",
                category: "Results-focused",
                popularity: "High",
                color: "cyan.500",
                image: templateImages.Achiever,
                component: AchieverTemplate,
                key: "Achiever",
            },
            {
                id: 8,
                name: "Impact",
                category: "Corporate",
                popularity: "High",
                color: "pink.500",
                image: templateImages.Impact,
                component: ImpactTemplate,
                key: "Impact",
            },
        ],
        [],
    )

    const selectedTemplateMeta = resumeTemplates.find((t) => t.key === selectedTemplateKey)
    const SelectedTemplateComponent = selectedTemplateMeta?.component || null

    const openPreview = (key) => {
        setSelectedTemplateKey(key)
        setIsPreviewLoading(true)
        onOpen()
        setTimeout(() => setIsPreviewLoading(false), 300)
    }

    // Normalize active builder list (API might return in .data or .data.response.data)
    const activeTemplatesList = useMemo(() => {
        const direct = activeResumeBuilderData?.data
        const nested = activeResumeBuilderData?.data?.response?.data
        const list = Array.isArray(nested) ? nested : Array.isArray(direct) ? direct : []
        return list
    }, [activeResumeBuilderData])

    const identityMapping = useMemo(
        () => ({
            professional: "SoftwareDeveloper",
            creative: "Creative",
            minimal: "Minimal",
            modern: "Modern",
            simple: "Simple",
            Executive: "Executive",
            Achiever: "Achiever",
            Impact: "Impact",
        }),
        [],
    )

    const isTemplateActive = (key) => {
        const resumeIdentity = identityMapping[key]
        if (!resumeIdentity) return false
        return activeTemplatesList.some((item) => {
            const id1 = item?.resume_identity
            const id2 = item?.resume_builders?.resume_identity
            return id1 === resumeIdentity || id2 === resumeIdentity
        })
    }

    const activeBuilderCount = activeTemplatesList.length || 0

    const howItWorks = [
        {
            icon: Squares2X2Icon,
            title: "Browse & Preview Templates",
            desc: "Explore a variety of professional templates and preview them with dummy content to see how your resume will look.",
        },
        {
            icon: DocumentTextIcon,
            title: "Click 'Use Template'",
            desc: "Select a template you like and replace the dummy data with your personal details such as education, experience, and skills.",
        },
        {
            icon: WrenchScrewdriverIcon,
            title: "Customize & Adjust",
            desc: "Fine-tune sections, reorder content, or make quick edits to tailor the resume to your preference before finalizing.",
        },
        {
            icon: ArrowDownTrayIcon,
            title: "Download Instantly",
            desc: "Your resume is generated instantly and downloaded as a crisp, high-quality PDF—ready to use anywhere.",
        },
        {
            icon: RocketLaunchIcon,
            title: "Replace & Upload",
            desc: "At the same time, a dialogue opens allowing you to replace your old resume in the system and upload the newly downloaded one.",
        },
    ];



    const features = [
        {
            icon: CheckCircleIcon,
            title: "ATS-friendly Design",
            desc: "Clean formatting and structure that parsing systems love.",
        },
        {
            icon: ChartBarIcon,
            title: "Optimized Sections",
            desc: "Work experience, skills, projects, achievements — laid out to impress.",
        },
        {
            icon: StarIcon,
            title: "Curated Templates",
            desc: "Executive, Creative, Minimal, Impact — pick the style that fits your brand.",
        },
    ]

    const faqs = [
        {
            q: "Can I preview templates before using them?",
            a: "Yes, you can preview all templates with default dummy data to see how they look before inserting your own details.",
        },
        {
            q: "How do I use a template with my own data?",
            a: "Simply click 'Use Template' and the dummy content will be replaced with your details like education, experience, and skills that you’ve already provided in your profile.",
        },
        {
            q: "Can I customize the resume after applying my details?",
            a: "Absolutely. You can reorder sections, polish the content, and make quick edits before finalizing the template.",
        },
        {
            q: "How do I download my resume?",
            a: "With one click, your resume is instantly generated as a high-quality PDF and downloaded to your system.",
        },
        {
            q: "What happens after I download?",
            a: "At the same time, a dialogue will open in your dashboard where you can replace your old resume with the new one and upload the fresh PDF.",
        },
    ];


    return (
        <Box bg={bgSoft}>
            {/* Hero */}
            <Box
                bgGradient="linear(to-r, blue.600, blue.700)"
                color="white"
                py={{ base: 14, md: 14 }}
                position="relative"
                overflow="hidden"
            >
                <Container maxW="7xl">
                    <Grid templateColumns={{ base: "1fr", md: "1.2fr 1fr" }} gap={10} alignItems="center">
                        <VStack align="start" spacing={5}>
                            <Tag size="lg" borderRadius="full" colorScheme="whiteAlpha" px={4} py={2}>
                                New • Resume Builder by IndiaHirings
                            </Tag>
                            <Heading className="text-left" as="h1" size="2xl" lineHeight="1.1">
                                Build a standout, ATS-friendly resume in minutes
                            </Heading>
                            <Text fontSize="lg" color="whiteAlpha.900">
                                Choose a template, add your details, and download a high-quality PDF — all from your dashboard.
                            </Text>
                            <HStack spacing={4} pt={2} wrap="wrap">
                                <Link to="/candidate-dashboard/resume">
                                    <Button size="lg" colorScheme="whiteAlpha" rightIcon={<ArrowRightIcon className="h-5 w-5" />}>
                                        Get Started
                                    </Button>
                                </Link>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    colorScheme="whiteAlpha"
                                    onClick={() => openPreview("professional")}
                                >
                                    Preview Templates
                                </Button>
                            </HStack>
                            <HStack spacing={6} pt={6}>
                                <HStack className="">
                                    <CheckCircleIcon className="h-5 w-5" />
                                    <div>8 Premium Templates</div>
                                </HStack>
                                <HStack>
                                    <CheckCircleIcon className="h-5 w-5" />
                                    <div>ATS-Friendly</div>
                                </HStack>
                                <HStack>
                                    <CheckCircleIcon className="h-5 w-5" />
                                    <div>PDF Export</div>
                                </HStack>
                            </HStack>
                        </VStack>

                        <Box
                            bg="white"
                            color="gray.800"
                            borderRadius="xl"
                            p={2}
                            boxShadow="2xl"
                            transform={{ md: "rotate(1deg)" }}
                            overflow="hidden"
                        >
                            <Image
                                src={templateImages.Achiever}
                                alt="Resume template preview"
                                borderRadius="lg"
                                objectFit="fill"
                                w="100%"
                                h={{ base: "260px", md: "540px" }}
                            />
                        </Box>
                    </Grid>
                </Container>
            </Box>

            {/* How It Works */}
            <Container maxW="7xl" py={{ base: 12, md: 16 }}>
                <VStack spacing={2} textAlign="center" mb={10}>
                    <Heading size="xl" color={textColor}>
                        How it works
                    </Heading>
                    <Text color={muted}>Create a compelling resume with a simple, guided flow.</Text>
                </VStack>
                <SimpleGrid columns={{ base: 1, md: 3, lg: 5 }} spacing={6}>
                    {howItWorks.map((step, idx) => (
                        <Box
                            key={step.title}
                            bg={cardBg}
                            p={6}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor={borderSoft}
                            transition="transform 0.2s"
                            _hover={{ transform: "translateY(-4px)" }}
                        >
                            <Flex align="center" mb={3} gap={3}>
                                <Box bg="blue.50" color="blue.600" p={3} borderRadius="lg">
                                    <step.icon className="h-6 w-6" />
                                </Box>
                                <Badge colorScheme="blue" variant="subtle">
                                    Step {idx + 1}
                                </Badge>
                            </Flex>
                            <Heading size="md" mb={2}>
                                {step.title}
                            </Heading>
                            <Text color={muted}>{step.desc}</Text>
                        </Box>
                    ))}
                </SimpleGrid>
            </Container>

            {/* Templates Gallery */}
            <Container maxW="7xl" py={{ base: 10, md: 14 }}>
                <Flex justify="space-between" align="center" wrap="wrap" mb={6}>
                    <VStack align="start" spacing={1}>
                        <Heading size="xl" color={textColor}>
                            Explore templates
                        </Heading>
                        <Text color={muted}>
                            From corporate to creative — pick a style that fits your story.
                        </Text>
                    </VStack>
                    <HStack gap={2}>
                        <Tag colorScheme="blue" borderRadius="full">
                            8 Templates
                        </Tag>

                    </HStack>
                </Flex>
                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6}>
                    {resumeTemplates.map((tpl) => {
                        const ready = isTemplateActive(tpl.key)
                        return (
                            <Box
                                key={tpl.id}
                                bg={cardBg}
                                borderRadius="xl"
                                overflow="hidden"
                                boxShadow="md"
                                border="1px solid"
                                borderColor={borderSoft}
                                transition="transform 0.2s, box-shadow 0.2s"
                                _hover={{ transform: "translateY(-6px)", boxShadow: "lg" }}
                            >
                                <Box h="220px" position="relative" bg={`linear-gradient(to right, ${tpl.color}, ${tpl.color}20)`}>
                                    <Image
                                        src={tpl.image || "/placeholder.svg"}
                                        alt={tpl.name}
                                        className="absolute inset-0 w-full h-full object-contain p-3"
                                    />
                                    <Box position="absolute" top="2" left="2" display="flex" gap="6px">
                                        <Badge colorScheme="blue">{tpl.category}</Badge>
                                        {ready && (
                                            <Badge colorScheme="green" variant="solid">
                                                Ready
                                            </Badge>
                                        )}
                                    </Box>
                                </Box>
                                <Box p={5}>
                                    <Flex justify="space-between" align="start" mb={2}>
                                        <Heading size="md">{tpl.name}</Heading>
                                        <Flex>
                                            {[...Array(5)].map((_, i) => (
                                                <StarIcon key={i} className={`h-4 w-4 ${i < 4 ? "text-yellow-400" : "text-gray-300"}`} />
                                            ))}
                                        </Flex>
                                    </Flex>
                                    <Text color={muted} mb={4}>
                                        {tpl.popularity} popularity
                                    </Text>
                                    <VStack spacing={2}>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            leftIcon={<EyeIcon className="h-4 w-4" />}
                                            onClick={() => openPreview(tpl.key)}
                                            width="100%"
                                            borderRadius="md"
                                        >
                                            Preview
                                        </Button>
                                        <Link to="/candidate-dashboard/resume">
                                            <Button
                                                size="sm"
                                                colorScheme={ready ? "green" : "blue"}
                                                rightIcon={<ArrowRightIcon className="h-4 w-4" />}
                                                width="100%"
                                                borderRadius="md"
                                            >
                                                {ready ? "Open in Dashboard" : "Use in Dashboard"}
                                            </Button>
                                        </Link>
                                    </VStack>
                                </Box>
                            </Box>
                        )
                    })}
                </Grid>
            </Container>

            {/* Features */}
            <Container maxW="7xl" py={{ base: 10, md: 14 }}>
                <VStack spacing={2} textAlign="center" mb={10}>
                    <Heading size="xl" color={textColor}>
                        Why candidates love our builder
                    </Heading>
                </VStack>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    {features.map((f) => (
                        <Box
                            key={f.title}
                            bg={cardBg}
                            p={6}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor={borderSoft}
                        >
                            <Box bg="green.50" color="green.600" p={3} borderRadius="md" w="fit-content" mb={3}>
                                <f.icon className="h-6 w-6" />
                            </Box>
                            <Heading size="md" mb={2}>
                                {f.title}
                            </Heading>
                            <Text color={muted}>{f.desc}</Text>
                        </Box>
                    ))}
                </SimpleGrid>
            </Container>

            {/* FAQ */}
            <Container maxW="7xl" py={{ base: 10, md: 14 }}>
                <VStack spacing={2} textAlign="center" mb={8}>
                    <Heading size="xl" color={textColor}>
                        Frequently asked questions
                    </Heading>
                    <Text color={muted}>
                        Answers to common questions about the resume builder.
                    </Text>
                </VStack>
                <Box
                    bg={cardBg}
                    p={6}
                    borderRadius="xl"
                    boxShadow="md"
                    border="1px solid"
                    borderColor={borderSoft}
                >
                    <Accordion allowToggle>
                        {faqs.map((f, i) => (
                            <AccordionItem key={i} border="none">
                                <h2>
                                    <AccordionButton
                                        px={0}
                                        py={4}
                                        _hover={{ bg: "transparent" }}
                                        borderBottom="1px solid"
                                        borderColor={borderSoft}
                                    >
                                        <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                                            {f.q}
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel px={0} py={4} color={muted}>
                                    {f.a}
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Box>
            </Container>

            {/* CTA Section */}
            <Container maxW="7xl" pb={{ base: 16, md: 24 }}>
                <Box
                    bgGradient="linear(to-r, blue.600, blue.700)"
                    borderRadius="2xl"
                    p={{ base: 8, md: 12 }}
                    color="white"
                    boxShadow="xl"
                    className=""
                >
                    <Grid templateColumns={{ base: "1fr", md: "1fr auto" }} gap={6} alignItems="center">
                        <VStack align="start" spacing={3}>
                            <Heading size="lg">Ready to build your resume?</Heading>
                            <Text color="whiteAlpha.900">
                                Choose a template and start editing in your candidate dashboard.
                            </Text>
                        </VStack>
                        <HStack spacing={3} className="flex flex-wrap">
                            <Link to="/candidate-dashboard/resume">
                                <Button size="lg" colorScheme="whiteAlpha" rightIcon={<ArrowRightIcon className="h-5 w-5" />}>
                                    Open Resume Builder
                                </Button>
                            </Link>
                            <Button
                                size="lg"
                                variant="outline"
                                colorScheme="whiteAlpha"
                                onClick={() => openPreview("minimal")}
                            >
                                See a Live Preview
                            </Button>
                        </HStack>
                    </Grid>
                </Box>
            </Container>

            {/* Preview Modal (uses actual template components + dummyData) */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="5xl">
                <ModalOverlay />
                <ModalContent borderRadius="xl">
                    <ModalHeader>
                        <Flex justify="space-between" align="center">
                            <Text>Template Preview</Text>
                            {selectedTemplateMeta && (
                                <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                                    {selectedTemplateMeta.name}
                                </Badge>
                            )}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {isPreviewLoading || !SelectedTemplateComponent ? (
                            <Flex justify="center" align="center" h="60vh" direction="column" gap={4}>
                                <Spinner size="xl" color="blue.500" thickness="4px" />
                                <Text>Loading preview...</Text>
                            </Flex>
                        ) : (
                            <Box
                                ref={previewRef}
                                bg="white"
                                p={4}
                                boxShadow="md"
                                borderRadius="md"
                                className="print-container"
                                maxH="70vh"
                                overflowY="auto"
                            >
                                <SelectedTemplateComponent candidateData={dummyData} />
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter bg="gray.50">
                        <HStack spacing={3}>
                            <Link to="/candidate-dashboard/resume">
                                <Button colorScheme="blue" rightIcon={<ArrowRightIcon className="h-5 w-5" />}>
                                    Use This Template
                                </Button>
                            </Link>
                            <Button variant="ghost" onClick={onClose}>
                                Close
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Footer */}

        </Box>
    )
}

export default ResumeBuilderHome